import { accessDebug, accessDebugError, accessDebugWarn } from "./accessDebug";

export const cookieName = (name) => `${(process.env.REACT_APP_COOKIE_PREFIX || "").trim()}${name}`;

const AUTH_COOKIE_NAMES = ["access_token", "refresh_token", "session_id", "siteauthtoken"].map(cookieName);

export const getCookie = (name) => {
  if (typeof document === "undefined") {
    accessDebugWarn("AuthSession", "cookie read skipped: document unavailable", { name });
    return "";
  }
  const value = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1] || "";
  if (name === cookieName("session_id")) {
    accessDebug("AuthSession", "session cookie visibility checked", {
      visibleToJavaScript: Boolean(value),
      note: "HttpOnly cookies are expected to be invisible here",
    });
  }
  return value;
};

export const getAuthSession = () => {
  const sessionId = getCookie(cookieName("session_id"));
  let userId = null;
  let userIdSource = "missing";
  let hasStoreSessionId = false;
  
  // Check Zustand store dynamically to avoid circular dependencies
  try {
    const { useAuth } = require("../store/useAuth");
    const state = useAuth.getState();
    userId = state?.user?.id || state?.userId || null;
    userIdSource = state?.user?.id
      ? "auth-store-user"
      : state?.userId
        ? "auth-store-user-id"
        : "missing";
    hasStoreSessionId = Boolean(state?.sessionId);
  } catch (e) {
    accessDebug("AuthSession", "auth store unavailable during session read", {
      errorMessage: e?.message,
    });
  }

  accessDebug("AuthSession", "session snapshot resolved", {
    userId,
    userIdSource,
    hasJavaScriptVisibleSessionId: Boolean(sessionId),
    hasStoreSessionId,
  });
  return { userId, sessionId };
};

export const getAuthUserId = () => {
  const session = getAuthSession();
  if (session?.userId) return session.userId;
  return null;
};

export const hasAuthCookies = () => {
  if (getCookie(cookieName("session_id"))) {
    accessDebug("AuthSession", "session presence result", {
      hasSession: true,
      source: "javascript-visible-cookie",
    });
    return true;
  }
  try {
    const { useAuth } = require("../store/useAuth");
    const state = useAuth.getState();
    const hasSession = Boolean(state?.sessionId);
    accessDebug("AuthSession", "session presence result", {
      hasSession,
      source: hasSession ? "auth-store" : "none",
      isAuthLoaded: state?.isAuthLoaded,
      hasUser: Boolean(state?.user),
    });
    return hasSession;
  } catch (e) {
    accessDebugError("AuthSession", "session presence check failed", e);
    return false;
  }
};

export const buildLoginRedirect = () => {
  if (typeof window === "undefined") return "/login";
  const { pathname, search } = window.location;
  if (pathname.startsWith("/login")) {
    const loginPath = `/login${search}`;
    accessDebug("AuthSession", "login redirect built", {
      reason: "already-on-login",
      destination: loginPath,
    });
    return loginPath;
  }
  const target = `${pathname}${search}`;
  const destination = `/login?redirect=${encodeURIComponent(target)}`;
  accessDebug("AuthSession", "login redirect built", { target, destination });
  return destination;
};

export const getPostLoginRedirect = () => {
  if (typeof window === "undefined") return null;
  let raw;
  try {
    raw = new URLSearchParams(window.location.search).get("redirect");
  } catch (e) {
    return null;
  }
  if (!raw) return null;
  let target;
  try {
    target = decodeURIComponent(raw);
  } catch (e) {
    return null;
  }
  if (!target.startsWith("/") || target.startsWith("//") || target.startsWith("/\\")) {
    return null;
  }
  return target;
};

export const clearAuthSessionStorage = () => {
  accessDebugWarn("AuthSession", "clearing client auth/session storage");
  if (typeof window !== "undefined") {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      accessDebugError("AuthSession", "failed to clear web storage", e);
    }
  }
  if (typeof document !== "undefined") {
    const hostname = window.location.hostname;
    const hostParts = hostname.split(".");
    const parentLabelCount =
      hostParts.length > 3 && hostParts.slice(-2).join(".") === "co.in" ? 3 : 2;
    const parentDomain =
      hostParts.length > parentLabelCount
        ? `.${hostParts.slice(-parentLabelCount).join(".")}`
        : `.${hostname}`;
    const domains = Array.from(new Set([hostname, `.${hostname}`, parentDomain]));

    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0]?.trim();
      if (!name) return;
      document.cookie = `${name}=; Max-Age=0; path=/`;
      if (!AUTH_COOKIE_NAMES.includes(name)) return;
      domains.forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
      });
    });
  }
};

export const requestServerSessionClear = async (apiUrl) => {
  if (!apiUrl || typeof fetch === "undefined") return;
  try {
    await fetch(`${apiUrl}/auth/clear-session`, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (e) {
    accessDebugError("AuthSession", "server session clear request failed", e);
  }
};
